
import { useMemo, useState } from 'react';
import { ChevronDown, RotateCcw, Search, Upload, X } from 'lucide-react';

import { Button, Card, Input, Seg, Select } from '../../components/ui';
import type { InsertMediaTypeFilter, LibraryFacets, TagsMode } from '../../types/api';
import {
  buildChips,
  hasQuery,
  SORT_LABELS,
  SORT_ORDER,
  effectiveSort,
  type FilterSection,
  type LibraryFilters,
} from './filters';
import styles from './LibraryPage.module.css';

interface Props {
  filters: LibraryFilters;
  onChange: (patch: Partial<LibraryFilters>) => void;
  queryDraft: string;
  onQueryDraft: (value: string) => void;
  onReset: () => void;
  facets: LibraryFacets | undefined;
  facetsLoading: boolean;
  found: number | null;
  loading: boolean;
  onUploadClick: () => void;
  uploadBlockedReason: string | null;
  problems: string[];
}

export function LibraryToolbar({
  filters,
  onChange,
  queryDraft,
  onQueryDraft,
  onReset,
  facets,
  facetsLoading,
  found,
  loading,
  onUploadClick,
  uploadBlockedReason,
  problems,
}: Props) {
  const [openSection, setOpenSection] = useState<FilterSection | null>(null);
  const chips = useMemo(() => buildChips(filters), [filters]);
  const withQuery = hasQuery(filters);
  const activeCount = chips.filter((chip) => chip.active).length;

  return (
    <Card>
      <div className={styles.toolbarRow}>
        <div className={styles.search}>
          <Search size={14} strokeWidth={1.5} className={styles.searchIcon} />
          <Input
            className={styles.searchInput}
            type="search"
            value={queryDraft}
            onChange={(event) => onQueryDraft(event.target.value)}
            placeholder="Поиск по смыслу: «врач объясняет пациенту»"
            maxLength={500}
            aria-label="Поиск по библиотеке"
          />
          {queryDraft && (
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              className={styles.searchClear}
              onClick={() => onQueryDraft('')}
              aria-label="Очистить поиск"
            >
              <X size={13} strokeWidth={1.8} />
            </Button>
          )}
        </div>

        <Select
          className={styles.sortSelect}
          aria-label="Порядок выдачи"
          value={effectiveSort(filters)}
          onChange={(event) => onChange({ sort: event.target.value as LibraryFilters['sort'] })}
        >
          {SORT_ORDER.map((key) => (
            <option key={key} value={key} disabled={key === 'relevance' && !withQuery}>
              {SORT_LABELS[key]}
              {key === 'relevance' && !withQuery ? ' (нужен запрос)' : ''}
            </option>
          ))}
        </Select>

        <Button
          variant="primary"
          onClick={onUploadClick}
          disabled={Boolean(uploadBlockedReason)}
          title={uploadBlockedReason ?? undefined}
        >
          <Upload size={13} strokeWidth={1.6} />
          Загрузить
        </Button>
      </div>

      <div className={styles.chipRow}>
        {chips.map((chip) => (
          <Button
            key={chip.section}
            size="sm"
            variant={chip.active ? 'primary' : 'default'}
            className={openSection === chip.section ? styles.chipOpen : undefined}
            onClick={() =>
              setOpenSection((current) => (current === chip.section ? null : chip.section))
            }
            aria-expanded={openSection === chip.section}
          >
            <span className={styles.chipKey}>{chip.label}</span>
            <span className={styles.chipValue}>{chip.value}</span>
            {chip.note ? <span className={styles.chipNote}>{chip.note}</span> : null}
            <ChevronDown size={11} strokeWidth={1.8} className={styles.chipChevron} />
          </Button>
        ))}

        <span className={styles.chipTail}>
          <span className={styles.foundCount} aria-busy={loading}>
            {found === null
              ? '…'
              : facets && found < facets.total
                ? `${found} из ${facets.total}`
                : String(found)}
          </span>
          <Button size="sm" onClick={onReset} disabled={activeCount === 0 && !withQuery}>
            <RotateCcw size={12} strokeWidth={1.6} />
            Сбросить
          </Button>
        </span>
      </div>

      {problems.length > 0 && (
        <div className={styles.problems} role="alert">
          {problems.map((problem) => (
            <div key={problem}>{problem}</div>
          ))}
        </div>
      )}

      {openSection && (
        <SectionEditor
          section={openSection}
          filters={filters}
          onChange={onChange}
          facets={facets}
          facetsLoading={facetsLoading}
        />
      )}
    </Card>
  );
}

interface EditorProps {
  section: FilterSection;
  filters: LibraryFilters;
  onChange: (patch: Partial<LibraryFilters>) => void;
  facets: LibraryFacets | undefined;
  facetsLoading: boolean;
}

function SectionEditor({ section, filters, onChange, facets, facetsLoading }: EditorProps) {
  if (section === 'tags') {
    return (
      <FacetSection
        title="теги"
        values={facets?.tags}
        loading={facetsLoading}
        selected={filters.tags}
        onToggle={(value) => onChange({ tags: toggle(filters.tags, value) })}
        emptyText="Тегов пока нет."
        head={
          <Seg<TagsMode>
            ariaLabel="Как сочетать теги"
            value={filters.tagsMode}
            options={[
              { value: 'all', label: 'Все выбранные' },
              { value: 'any', label: 'Любой из них' },
            ]}
            onChange={(mode) => onChange({ tagsMode: mode })}
          />
        }
      />
    );
  }

  if (section === 'mood') {
    return (
      <FacetSection
        title="настроение"
        values={facets?.mood}
        loading={facetsLoading}
        selected={filters.mood}
        onToggle={(value) => onChange({ mood: toggle(filters.mood, value) })}
        emptyText="Настроение пока не проставлено."
        head={<span className={styles.sectionHint}>подходит любое</span>}
      />
    );
  }

  if (section === 'geo') {
    return (
      <FacetSection
        title="гео"
        values={facets?.geo}
        loading={facetsLoading}
        selected={filters.geo}
        onToggle={(value) => onChange({ geo: toggle(filters.geo, value) })}
        emptyText="Гео пока не заполнено."
        head={<span className={styles.sectionHint}>подходит любое</span>}
      />
    );
  }

  if (section === 'mediaType') {
    return (
      <SectionShell title="тип материала">
        <Seg<InsertMediaTypeFilter>
          ariaLabel="Тип материала"
          value={filters.mediaType}
          options={[
            { value: 'any', label: `Всё${facets ? ` · ${facets.total}` : ''}` },
            { value: 'video', label: `Видео${facets ? ` · ${facets.videos}` : ''}` },
            { value: 'photo', label: `Фото${facets ? ` · ${facets.photos}` : ''}` },
          ]}
          onChange={(mediaType) => onChange({ mediaType })}
        />
      </SectionShell>
    );
  }

  if (section === 'duration') {
    return (
      <SectionShell title="длительность">
        <div className={styles.rangeRow}>
          <Input
            className={styles.rangeInput}
            size="sm"
            mono
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={filters.durationMin}
            onChange={(event) => onChange({ durationMin: event.target.value })}
            placeholder="от"
            aria-label="Длительность от, секунд"
          />
          <span className={styles.rangeDash}>–</span>
          <Input
            className={styles.rangeInput}
            size="sm"
            mono
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={filters.durationMax}
            onChange={(event) => onChange({ durationMax: event.target.value })}
            placeholder="до"
            aria-label="Длительность до, секунд"
          />
          <span className={styles.sectionHint}>в секундах, фото не попадают</span>
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell title="дата добавления">
      <div className={styles.rangeRow}>
        <Input
          className={styles.dateInput}
          size="sm"
          mono
          type="date"
          value={filters.createdFrom}
          onChange={(event) => onChange({ createdFrom: event.target.value })}
          aria-label="Добавлено с"
        />
        <span className={styles.rangeDash}>–</span>
        <Input
          className={styles.dateInput}
          size="sm"
          mono
          type="date"
          value={filters.createdTo}
          onChange={(event) => onChange({ createdTo: event.target.value })}
          aria-label="Добавлено по"
        />
      </div>
    </SectionShell>
  );
}

function SectionShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>{title}</span>
        {children}
      </div>
    </div>
  );
}

interface FacetSectionProps {
  title: string;
  values: { value: string; count: number }[] | undefined;
  loading: boolean;
  selected: string[];
  onToggle: (value: string) => void;
  emptyText: string;
  head: React.ReactNode;
}

function FacetSection({
  title,
  values,
  loading,
  selected,
  onToggle,
  emptyText,
  head,
}: FacetSectionProps) {
  const [needle, setNeedle] = useState('');
  const visible = useMemo(() => {
    const list = values ?? [];
    const trimmed = needle.trim().toLowerCase();
    if (!trimmed) return list;
    return list.filter((item) => item.value.toLowerCase().includes(trimmed));
  }, [values, needle]);

  const orphans = selected.filter((value) => !(values ?? []).some((item) => item.value === value));

  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>{title}</span>
        {head}
        {(values?.length ?? 0) > 12 && (
          <Input
            className={styles.facetFilter}
            size="sm"
            type="search"
            value={needle}
            onChange={(event) => setNeedle(event.target.value)}
            placeholder="фильтр по списку"
            aria-label={`Фильтр списка: ${title}`}
          />
        )}
      </div>
      <div className={styles.facetList}>
        {loading && <span className={styles.sectionHint}>Загружаем…</span>}
        {!loading && (values?.length ?? 0) === 0 && (
          <span className={styles.sectionHint}>{emptyText}</span>
        )}
        {!loading && (values?.length ?? 0) > 0 && visible.length === 0 && (
          <span className={styles.sectionHint}>Ничего похожего</span>
        )}
        {orphans.map((value) => (
          <Button
            key={`orphan-${value}`}
            size="sm"
            variant="primary"
            className={styles.facet}
            onClick={() => onToggle(value)}
          >
            {value}
            <span className={styles.facetCount}>вне списка</span>
          </Button>
        ))}
        {visible.map((item) => {
          const on = selected.includes(item.value);
          return (
            <Button
              key={item.value}
              size="sm"
              variant={on ? 'primary' : 'default'}
              className={styles.facet}
              onClick={() => onToggle(item.value)}
              aria-pressed={on}
            >
              {item.value}
              <span className={styles.facetCount}>{item.count}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function toggle(list: readonly string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}
